import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getCounselorSessions,
  getSessionDetails,
  updateSessionStatus,
  getSessionRequests,
  approveSessionRequest,
  rejectSessionRequest
} from '@/services/sessions'

// Query keys
export const sessionKeys = {
  all: ['sessions'] as const,
  lists: () => [...sessionKeys.all, 'list'] as const,
  list: (params?: any) => [...sessionKeys.lists(), params] as const,
  details: () => [...sessionKeys.all, 'detail'] as const,
  detail: (id: string) => [...sessionKeys.details(), id] as const,
  requests: (params?: any) => [...sessionKeys.all, 'requests', params] as const,
}

// Get counselor sessions
export const useCounselorSessions = (params?: any) => {
  return useQuery({
    queryKey: sessionKeys.list(params),
    queryFn: () => getCounselorSessions(params),
    staleTime: 1 * 60 * 1000, // 1 minute
  })
}

// Get session details
export const useSessionDetails = (sessionId?: string) => {
  return useQuery({
    queryKey: sessionKeys.detail(sessionId || ''),
    queryFn: () => getSessionDetails(sessionId!),
    enabled: !!sessionId,
    staleTime: 30 * 1000, // 30 seconds
  })
}

// Get session requests
export const useSessionRequests = (userId?: string, params?: any) => {
  return useQuery({
    queryKey: sessionKeys.requests({ userId, ...params }),
    queryFn: () => getSessionRequests(userId, params),
    enabled: !!userId, // Only fetch if userId is provided
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 60 * 1000, // Refetch every minute for new requests
  })
}

// Update session status
export const useUpdateSessionStatus = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ sessionId, data }: { sessionId: string; data: any }) =>
      updateSessionStatus(sessionId, data),
    onSuccess: (data, variables) => {
      // Update session details cache
      queryClient.setQueryData(sessionKeys.detail(variables.sessionId), data)
      
      // Invalidate session lists to refresh
      queryClient.invalidateQueries({ queryKey: sessionKeys.lists() })
      queryClient.invalidateQueries({ queryKey: sessionKeys.requests() })
    },
  })
}

// Approve session request
export const useApproveSessionRequest = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ requestId, data }: { requestId: string; data: any }) =>
      approveSessionRequest(requestId, data),
    onSuccess: () => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: sessionKeys.requests() })
      queryClient.invalidateQueries({ queryKey: sessionKeys.lists() })
    },
  })
}

// Reject session request
export const useRejectSessionRequest = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ requestId, reason }: { requestId: string; reason: string }) =>
      rejectSessionRequest(requestId, reason),
    onSuccess: () => {
      // Invalidate session requests
      queryClient.invalidateQueries({ queryKey: sessionKeys.requests() })
    },
  })
}